import { useMutation, useQueryClient } from "@tanstack/react-query";
import { sendMessage, SendMessagePayload } from "@/api/chats";
import { ChatsQueryKeys } from "@/tanstackQuery/queries/chats";
import { Message } from "@/types/ApiResponses/Message";

export const useSendMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: SendMessagePayload) => sendMessage(payload),
    // After the server confirms, push the freshly-created message into the
    // active chat's message cache immediately and refetch the chat list. We
    // rely on the immediate cache write rather than refetch-only because the
    // socket "newMessage" event also invalidates the same query and racing
    // refetches sometimes return stale data.
    onSuccess: (data) => {
      const chatId = data?.chat?.id;
      if (chatId !== undefined && data?.message) {
        const newMessage = data.message as Message;
        queryClient.setQueryData<Message[]>(
          [ChatsQueryKeys.ChatMessages, chatId],
          (prev) => {
            if (!prev) return [newMessage];
            // Skip if the socket event already inserted it.
            if (prev.some((m) => m.id === newMessage.id)) return prev;
            return [...prev, newMessage];
          }
        );
      }
      // Background refetches keep paginated views and the chat list aligned.
      queryClient.invalidateQueries({
        queryKey: [ChatsQueryKeys.ChatMessagesPaginated],
      });
      queryClient.invalidateQueries({
        queryKey: [ChatsQueryKeys.ChatMessages],
      });
      queryClient.invalidateQueries({
        queryKey: [ChatsQueryKeys.Chats],
      });
      queryClient.invalidateQueries({
        queryKey: [ChatsQueryKeys.ChatsPaginated],
      });
    },
  });
};
